<template>
  <div>
    <div class="detail-header">
      <h3>
        <span v-if="currentSubCategory.versionCode === 21">WCAG 2.1</span>
        <span v-else>WCAG 2.0</span>
        <em>{{ currentSubCategory.WcagCategory.id }}.
          {{ currentSubCategory.WcagCategory.titleEn }} [{{ currentSubCategory.WcagCategory.level }}]</em>
      </h3>
      <p class="desc">{{ currentSubCategory.WcagCategory.descEn }}</p>
    </div>

    <div v-if="!activeRules.length" class="no-by-case">
      <p>
        <i class="icon icon--empty"></i><br>
        {{ $t('report.noIssues') }}
      </p>
    </div>

    <div v-for="rule in activeRules"
         :key="rule.id"
         class="by-case"
         :class="rule.resultType === 1 ? 'pass' : 'error'">
      <h4 class="case-title">
            <span class="key">
              <i class="icon"
                 :class="`icon--${rule.resultType === 1 ? 'pass' : 'error'}`"></i>
              {{ ruleShown(rule.id) }}
            </span>
        <span class="name">Case :</span>
        <span class="content">{{ rule.descEn }}</span>
      </h4>

      <p v-if="rule.guideEn && rule.resultType === 2"
         class="case-guide">
        <span class="name">Guide :</span>
        <span class="content">{{ rule.guideEn }} (For more information, see SWCAG)</span>
      </p>

      <p v-if="rule.needCheck"
         class="only-check-case">
        <b><i class="icon icon--info"></i> Check :</b>
        <span class="check-desc">{{ rule.checkTextEn }}</span>
        (if not, <span class="check-result">{{ rule.checkResultType === 1 ? 'pass' : 'error' }}</span>)
      </p>

      <table class="case-table"
             style="table-layout: fixed">
        <colgroup>
          <col width="5%" />

          <template v-if="ruleUiType[rule.uiTypeCode]">
            <col v-for="(columnData, index) in ruleUiType[rule.uiTypeCode]"
                 :width="columnData.width || '*'"
                 :key="index" />
          </template>

          <!--<col width="25%"
               v-if="report.ReportDatum.deviceType < 10" />-->
        </colgroup>

        <thead>
        <tr>
          <th scope="row">ID</th>
          <template v-if="ruleUiType[rule.uiTypeCode]">
            <th v-for="(columnData, index) in ruleUiType[rule.uiTypeCode]" :key="index" scope="row">
              {{ columnData.name }}
            </th>
          </template>

          <!--<th scope="row"
              v-if="report.ReportDatum.deviceType < 10">Code</th>-->
        </tr>
        </thead>
        <tbody>
        <template v-for="(item, index) in categoryResultMap[currentSubCategory.id][rule.id]">
          <tr :key="currentSubCategory.id + '-' + index"
              :class="{ active: currentIssue && currentIssue.id === item.id, hover: item.id && hovered === item.id }"
              @mouseenter="setHovered(item.id)"
              @mouseleave="setHovered(null)"
              @click="onIssueClick(item, rowspan(ruleUiType[rule.uiTypeCode], rule.id, index) > 1 ? 0 : null)">
            <td :rowspan="rowspan(ruleUiType[rule.uiTypeCode], rule.id, index)">
              <p class="nowrap gray">{{ index + 1 }}</p>
            </td>

            <template v-if="ruleUiType[rule.uiTypeCode]">
              <td v-for="(columnData, idx) in ruleUiType[rule.uiTypeCode]"
                  :rowspan="!columnData.array ? rowspan(ruleUiType[rule.uiTypeCode], rule.id, index) : null"
                  :key="idx">
                <template v-if="!columnData.type">
                  {{ columnData.array ? item.preview[columnData.field][0] : item.preview[columnData.field] }}
                </template>
                <template v-else-if="columnData.type === 'text'">
                  <p class="align-left">{{ columnData.array ? item.preview[columnData.field][0] : item.preview[columnData.field] }}</p>
                </template>
                <template v-else-if="columnData.type === 'ocrText'">
                  <p class="align-left">
                    <template v-if="(conf = (columnData.array ? item.preview['ocrConf'][0] : item.preview['ocrConf'])) || (conf === 0)">
                            <span class="conf"
                                  :style="{
                                    color: (conf < 30 ? '#444444' : '#ffffff'),
                                    backgroundColor: `rgba(0, 0, 0, ${conf *  0.01})`
                                  }">{{ conf }}%</span>
                      <br>
                    </template>
                    {{ columnData.array ? item.preview[columnData.field][0] : item.preview[columnData.field] }}
                  </p>
                </template>
                <template v-else-if="columnData.type === 'percent'">
                  <p class="align-right nowrap">{{ columnData.array ? item.preview[columnData.field][0] : item.preview[columnData.field] }}%</p>
                </template>
                <template v-else-if="columnData.type === 'static'">
                  {{ (columnData.array ? columnData.value[0] : columnData.value) || '-' }}
                </template>
                <template v-else-if="columnData.type === 'imageUrl' && item.preview && item.preview[columnData.field]">
                  <img :src="columnData.array ? item.preview[columnData.field][0] : item.preview[columnData.field]" alt="" />
                </template>
                <template v-else-if="columnData.type === 's3Image' && item.preview && item.preview[columnData.field]">
                  <img :src="columnData.array ? imageUrl[item.preview[columnData.field][0]] : imageUrl[item.preview[columnData.field]]" alt="" />
                </template>
                <template v-else-if="columnData.type === 'tagName'">
                  {{ (columnData.array ? item.preview[columnData.field][0] : item.preview[columnData.field]) | lowercase }}
                </template>
                <template v-else-if="columnData.type === 'code'">
                  {{ (columnData.array ? getCode(item.preview[columnData.field][0]) : getCode(item.id)) }}
                </template>
                <template v-else-if="columnData.type === 'match'">
                        <pre class="code"
                             v-html="cleanCode(item.preview[columnData.field])"></pre>
                </template>
                <template v-else-if="columnData.type === 'color'">
                  <span class="colorchip" :style="{ backgroundColor: columnData.array ? item.preview[columnData.field][0] : item.preview[columnData.field] }"></span>
                  <p class="nowrap gray" style="font-size: 12px">{{ columnData.array ? item.preview[columnData.field][0] : item.preview[columnData.field] }}</p>
                </template>
                <template v-else-if="columnData.type === 'ratio'">
                  <div class="ratio">
                    <div class="text-bg">
                      <div class="text-box"
                           :style="{
                                        color: textColor = item.preview['ocrColor'] ? (columnData.array ? item.preview['ocrColor'][0] : item.preview['ocrColor']) : (columnData.array ? item.preview['color'][0] : item.preview['color']),
                                        backgroundColor: bgColor = item.preview['ocrBgColor'] ? (columnData.array ? item.preview['ocrBgColor'][0] : item.preview['ocrBgColor']) : (columnData.array ? item.preview['bgColor'][0] : item.preview['bgColor'])
                                     }">TEXT</div>
                      <div class="color">
                        <div class="text">
                                    <span class="chip"
                                          :style="{ backgroundColor: textColor }"></span>
                          {{ textColor }}
                        </div>
                        <div class="text">
                                    <span class="chip"
                                          :style="{ backgroundColor: bgColor }"></span>
                          {{ bgColor }}
                        </div>
                      </div>
                    </div>
                    <div class="ratio-result">
                      <span class="red" v-if="(ratio = columnData.array ? item.preview[columnData.field][0] : item.preview[columnData.field]) < 3">Fail</span>
                      <span class="orange" v-else-if="ratio < 4">Fail</span>
                      <span class="yellow" v-else-if="ratio < 4.5">Fail</span>
                      <span class="green" v-else>Pass</span>
                      <br>
                      {{ ratio }}
                    </div>
                  </div>
                </template>
              </td>
            </template>

            <!--<td v-if="report.ReportDatum.deviceType < 10"
                :rowspan="rowspan(ruleUiType[rule.uiTypeCode], rule.id, index)">
              <div class="align-left">
                {{ getCode(item.id) }}
              </div>
            </td>-->
          </tr>
          <template v-if="rowspan(ruleUiType[rule.uiTypeCode], rule.id, index) > 1">
            <tr v-for="idx in (rowspan(ruleUiType[rule.uiTypeCode], rule.id, index) - 1)"
                :class="{ active: currentIssue && currentIssue.id === item.id, hover: item.id && hovered === item.id }"
                @mouseenter="setHovered(item.id)"
                @mouseleave="setHovered(null)"
                @click="onIssueClick(item, idx)"
                :key="`${index}-${idx}`">
              <template v-if="ruleUiType[rule.uiTypeCode]">
                <template v-for="(columnData, i) in ruleUiType[rule.uiTypeCode]">
                  <td v-if="columnData.array"
                      :key="i">
                    <template v-if="!columnData.type">{{ item.preview[columnData.field][idx] }}</template>
                    <template v-else-if="columnData.type === 'text'">
                      <p class="align-left">{{ item.preview[columnData.field][idx] }}</p>
                    </template>
                    <template v-else-if="columnData.type === 'ocrText'">
                      <p class="align-left">
                        <template v-if="(conf = item.preview['ocrConf'][idx]) || (conf === 0)">
                                <span class="conf"
                                      :style="{
                                        color: (conf < 30 ? '#444444' : '#ffffff'),
                                        backgroundColor: `rgba(0, 0, 0, ${conf * 0.01})`
                                      }">{{ conf }}%</span>
                          <br>
                        </template>
                        {{ item.preview[columnData.field][idx] }}
                      </p>
                    </template>
                    <template v-else-if="columnData.type === 'percent'">
                      <p class="align-right nowrap">{{ item.preview[columnData.field][idx] }}%</p>
                    </template>
                    <template v-else-if="columnData.type === 'static'">{{ columnData.value[idx] || '-' }}</template>
                    <template v-else-if="columnData.type === 'imageUrl' && item.preview && item.preview[columnData.field]">
                      <img :src="item.preview[columnData.field][idx]" alt="" />
                    </template>
                    <template v-else-if="columnData.type === 's3Image' && item.preview && item.preview[columnData.field]">
                      <img :src="imageUrl[item.preview[columnData.field][idx]]" alt="" />
                    </template>
                    <template v-else-if="columnData.type === 'tagName'">{{ item.preview[columnData.field][idx] | lowercase }}</template>
                    <template v-else-if="columnData.type === 'code'">{{ getCode(item.preview[columnData.field][idx]) }}</template>
                    <template v-else-if="columnData.type === 'match'">
                            <pre class="code"
                                 v-html="cleanCode(item.preview[columnData.field][idx])"></pre>
                    </template>
                    <template v-else-if="columnData.type === 'color'">
                      <span class="colorchip" :style="{ backgroundColor: item.preview[columnData.field][idx] }"></span>
                      <p class="nowrap gray" style="font-size: 12px">{{ item.preview[columnData.field][idx] }}</p>
                    </template>
                    <template v-else-if="columnData.type === 'ratio'">
                      <div class="ratio">
                        <div class="text-bg">
                          <div class="text-box"
                               :style="{
                                        color: textColor = item.preview['ocrColor'] ? item.preview['ocrColor'][idx] : item.preview['color'][idx],
                                        backgroundColor: bgColor = item.preview['ocrBgColor'] ? item.preview['ocrBgColor'][idx] : item.preview['bgColor'][idx]
                                     }">TEXT</div>
                          <div class="color">
                            <div class="text">
                                    <span class="chip"
                                          :style="{ backgroundColor: textColor }"></span>
                              {{ textColor }}
                            </div>
                            <div class="text">
                                    <span class="chip"
                                          :style="{ backgroundColor: bgColor }"></span>
                              {{ bgColor }}
                            </div>
                          </div>
                        </div>
                        <div class="ratio-result">
                          <span class="red" v-if="item.preview[columnData.field][idx] < 3">Fail</span>
                          <span class="orange" v-else-if="item.preview[columnData.field][idx] < 4">Fail</span>
                          <span class="yellow" v-else-if="item.preview[columnData.field][idx] < 4.5">Fail</span>
                          <span class="green" v-else>Pass</span>
                          <br>
                          {{ item.preview[columnData.field][idx] }}
                        </div>
                      </div>
                    </template>
                  </td>
                </template>
              </template>
            </tr>
          </template>
        </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'

export default {
  name: 'ReportIssueList',
  props: [
    'currentIssue',
    'nodeDataMap',
    'currentSubCategory',
    'categoryResultMap',
    'ruleUiType',
    'imageUrl'
  ],
  data () {
    return {
      rowspanCache: {},
      hovered: null
    }
  },
  computed: {
    activeRules () {
      return _.filter(this.currentSubCategory.Rules, rule => {
        const resultMap = this.categoryResultMap[this.currentSubCategory.id]
        return resultMap && resultMap[rule.id]
      })
    }
  },
  methods: {
    cleanCode (value) {
      const lineBreak = /;\r?\n|\r/
      if (!value || lineBreak.test(value)) {
        return value
      } else {
        return value.replace(/([;{}])/g, `$1
`)
      }
    },
    ruleShown (rule) {
      if (/[a-zA-Z]/.test(rule.slice(-1))) {
        return rule.slice(0, -1)
      }
      return rule
    },
    setHovered (value) {
      this.hovered = value
    },
    onIssueClick (issue, arrayIdx) {
      this.$emit('onIssueClick', issue, arrayIdx)
    },
    rowspan (col, rule, row) {
      if (this.rowspanCache[`${rule}-${row}`]) {
        return this.rowspanCache[`${rule}-${row}`]
      }

      let rowData = this.categoryResultMap[this.currentSubCategory.id][rule][row]
      let hasArray = false
      let arrayField = []
      for (const column of col) {
        if (column.array) {
          hasArray = true
          arrayField.push(column.field)
        }
      }
      if (!hasArray) {
        return null
      }

      let rowspan = 1
      for (const field of arrayField) {
        if (rowData.preview && rowData.preview[field]) {
          rowspan = Math.max(rowspan, rowData.preview[field].length)
        }
      }
      this.rowspanCache[`${rule}-${row}`] = rowspan
      return rowspan
    },
    getCode (id) {
      const nodeData = this.nodeDataMap[id]

      if (!nodeData) {
        return ''
      }

      let isSimpleTag

      switch (nodeData.tagName) {
        case 'A':
        case 'AREA':
        case 'IMG':
        case 'INPUT':
        case 'SELECT':
        case 'LABEL':
        case 'SPAN':
        case 'EM':
        case 'B':
        case 'STRONG':
        case 'I':
        case 'FIGCAPTION':
        case 'FIGURE':
        case 'H1':
        case 'H2':
        case 'H3':
        case 'H4':
        case 'H5':
        case 'H6':
        case 'TH':
        case 'TRACK':
        case 'VIDEO':
        case 'AUDIO':
        case 'BUTTON':
          isSimpleTag = true
          break
        default:
          isSimpleTag = false
      }

      if (isSimpleTag) {
        const codeEl = document.getElementById(`code-id-${id}`)
        if (codeEl) {
          return codeEl.innerText.trim()
        }
        return 'not available'
      } else {
        let attrString = ''
        let attrValues = []

        if (nodeData.attrs) {
          for (let [key, value] of Object.entries(nodeData.attrs)) {
            attrValues.push(`${key}="${value}"`)
          }
        }

        if (attrValues.length) {
          attrString = ' ' + attrValues.join(' ')
        }
        const tagName = nodeData.tagName.toLowerCase()
        return `<${tagName}${attrString}>...</${tagName}>`
      }
    }
  }
}

</script>

<style lang="scss" scoped>
.detail-header {
  border-bottom: 1px dashed #999999;
  padding-bottom: 19px;

  h3 {
    font-size: 14px;
    font-weight: 600;
    color: #767676;
    margin: 7px 0;
  }

  .desc {
    font-size: 13px;
    line-height: 1.31;
    color: #767676;
    max-width: 600px;
  }
}

.by-case {
  font-size: 15px;
  line-height: 1.33;
  color: #444444;
  margin: 30px 0;

  .case-title {
    font-weight: 500;
    margin-right: 30px;
    position: sticky;
    top: 105px;
    left: 0;
    z-index: 10;

    .key {
      display: inline-block;
      vertical-align: middle;
      padding: 8px 9px 8px 7px;
      width: 80px;
      white-space: nowrap;
      margin-right: 15px;
      border-radius: 6px;
      border: solid 2px #999999;
      background-color: #ffffff;
      font-size: 15px;
      font-weight: 600;
    }

    .name {
      display: inline-block;
      vertical-align: top;
      margin: 9px 10px 0 0;
      width: 46px;
      white-space: nowrap;
    }

    .content {
      display: inline-block;
      vertical-align: top;
      margin-top: 9px;
      width: calc(100% - 195px);
    }
  }

  .case-guide {
    margin-top: 6px;
    margin-left: 119px;
    margin-right: 30px;

    .name {
      display: inline-block;
      vertical-align: top;
      margin: 0 10px 0 0;
      width: 46px;
      white-space: nowrap;
    }

    .content {
      display: inline-block;
      vertical-align: top;
      width: calc(100% - 65px);
    }
  }

  .only-check-case {
    margin: 20px 0 20px 180px;
    padding: 10px 15px 15px;
    border-radius: 4px;
    border: solid 1px #ececb2;
    background-color: #ffffe3;
    font-size: 14px;
    line-height: 1.36;
    color: #863a13;

    b {
      display: block;
      font-weight: 500;
      line-height: 1.43;
      margin-top: 4px;
      margin-bottom: 1px;
    }
  }

  &.pass {
    .case-title {
      color: #3eaa08;

      span {
        border-color: #3eaa08;
      }
    }
  }

  &.error {
    .case-title {
      color: #ec0047;

      span {
        border-color: #ec0047;
      }
    }
  }
}

.case-table {
  width: 100%;
  margin-top: 33px;
  margin-bottom: 70px;

  th {
    font-size: 13px;
    font-weight: 500;
    color: #666666;
    border-bottom: 2px solid #666666;
    padding: 3px 0;
    text-align: center;
  }

  td {
    padding: 15px 5px;
    font-size: 13px;
    vertical-align: top;
    color: #444444;
    word-break: break-all;
    text-align: center;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
    transition: background-color 0.15s ease;
    cursor: pointer;

    img {
      max-width: 80%;
      border: 1px solid rgba(0, 0, 0, 0.1);
    }

    .colorchip {
      display: block;
      width: 28px;
      height: 28px;
      border: solid 1px rgba(0, 0, 0, 0.25);
      background-color: #886a4a;
      margin: 0 auto 2px;
      border-radius: 100%;
    }

    .nowrap {
      white-space: nowrap;
    }

    .align-right {
      text-align: right;
    }

    .align-left {
      text-align: left;
    }

    .code {
      background: #222222;
      color: #c1c1c1;
      font-family: monospace;
      text-align: left;
      padding: 5px 10px;
      margin: -5px 0;
      border-radius: 8px;
      overflow: auto;

      & /deep/ mark {
        background: #ffffff;
        color: #000000;
        font-family: monospace;
      }

      &::before {
        content: "···";
        text-align: center;
        color: #999999;
      }

      &::after {
        content: "···";
        text-align: center;
        color: #999999;
      }
    }

    .conf {
      display: inline-block;
      min-width: 32px;
      height: 18px;
      margin-bottom: 5px;
      border-radius: 3px;
      text-align: center;
      font-size: 12px;
      line-height: 18px;
    }

    .ratio {
      white-space: nowrap;

      .text-bg {
        position: relative;
        display: inline-block;
        white-space: nowrap;
        text-align: left;
        width: 200px;

        .text-box {
          width: 82px;
          height: 46px;
          text-align: center;
          line-height: 46px;
          font-weight: 700;
          font-size: 24px;
          display: inline-block;
          vertical-align: top;
          border: 1px solid rgba(0, 0, 0, 0.1);

          &::before {
            position: absolute;
            top: 0;
            left: 0;
            content: "";
            width: 49px;
            height: 35px;
            display: inline-block;
            background-image: url(~@/assets/asset--ratio-line.svg);
            margin: 7px 0 0 55px;
          }
        }

        .color {
          position: relative;
          display: inline-block;
          margin-left: 15px;
          vertical-align: top;
          margin-top: -1px;

          .text {
            font-size: 12px;
            color: #666666;
            text-transform: uppercase;

            .chip {
              display: inline-block;
              width: 13px;
              height: 13px;
              margin-bottom: 2px;
              border-radius: 2px;
              border: solid 1px rgba(0, 0, 0, 0.25);
              background-color: #e9d8bd;
              vertical-align: middle;
            }
          }
        }
      }
    }

    .ratio-result {
      display: inline-block;
      vertical-align: top;
      width: 65px;
      font-size: 24px;
      color: #444444;
      text-align: left;
      height: 45px;
      line-height: 0.9;

      span {
        display: inline-block;
        width: 36px;
        height: 18px;
        margin-bottom: 5px;
        border-radius: 3px;
        text-align: center;
        font-size: 12px;
        line-height: 18px;
        color: #ffffff;
        vertical-align: top;

        &.red {
          background-color: #e92020;
        }

        &.orange {
          background-color: #f36c00;
        }

        &.yellow {
          color: #984700;
          background-color: #ffd700;
        }

        &.green {
          background-color: #3eaa08;
        }
      }
    }
  }

  tr:last-child {
    td {
      border-bottom: 1px solid rgba(0, 0, 0, 0.25);
    }
  }
}

.by-case {
  &.error {
    tr:hover,
    tr.hover {
      td {
        background-color: rgba(255, 0, 0, 0.08);
      }
    }
  }

  &.pass {
    tr:hover,
    tr.hover {
      td {
        background-color: rgba(111, 234, 0, 0.16);
      }
    }
  }
}

.no-by-case {
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  p {
    text-align: center;
    font-size: 13px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1;
    letter-spacing: normal;
    color: #767676;
    padding: 70px 0;

    i {
      margin-bottom: 10px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/valid-member/report/ReportIssueListItem.vue